import type { QueryResultWithLimit } from '@nx/constants';
import { APP_SCOPE, QUERY_TYPE } from '@nx/constants';
import { type ParseableError, isParseableError } from '@nx/errors';
import { StoreKit, makeSelectFeatureFlag, runQuery } from '@nx/state';
import { createAsyncThunk } from '@reduxjs/toolkit';
import type * as DriverCore from 'neo4j-driver-core';

import { recordMapper } from '../../services/bolt/boltMappings';
import { V4_ID, V5_ID } from '../../services/bolt/constants';
import { UnknownErrors, isTransactionTimeoutError } from '../../services/errors/errors';
import { log } from '../../services/logging';
import { getSchemaFromPerspective } from '../../services/perspectives/schema';
import { isMappedNode } from '../../services/queries/mappers';
import {
  getNeighboursForIds,
  getRelevantRelationshipTypes,
  getSampleSchema,
} from '../../services/queries/queryHandler';
import { graphMapper } from '../../services/queries/resultMapper';
import type { Records } from '../../services/search/types';
import { trackUserQueryFromBloomScene, userQueryTypes } from '../../services/tracking';
import { isVersion5OorGreater } from '../../services/versions/versionUtils';
import type { Node, Relationship } from '../../types/graph';
import { getDatabase, getServerVersion } from '../connections/connectionsDuck';
import { showNodesAndRelationships } from '../graph/graph.thunks';
import { activateNodes, deactivateNodes, getSelectedNodes, getVisibleNodeIds } from '../graph/nodes';
import { getVisibleRelationshipIds } from '../graph/relationships';
import { getPathSegments } from '../perspectives/perspectiveMetadata';
import { getHiddenRelationshipTypes, getVisibleLabels } from '../perspectives/perspectives';
import { getPerspective } from '../perspectives/perspectives.selector';
import type { RootState } from '../types';
import { NAME } from './contextmenu';

type RunExpandPayload = {
  clickedNode?: Node;
  labels?: string[];
  relTypes?: string[];
  direction?: string;
  queryResultLimit: number;
  timeout: number;
  autoSelectNewNodes: boolean;
  isHttpQueryApiEnabled: boolean;
  trackUserQueryEvent?: string;
};
type ShowGraphPayload = {
  labels: string[];
  queryResultLimit: number;
  timeout: number;
};

const handleBlinking = (dispatch: any, nodeIdStrings: string[]) => {
  const BLINK_TIME = 1000;
  let startTime = Date.now();

  return {
    startBlinking: () => {
      dispatch(activateNodes(nodeIdStrings));
      startTime = Date.now();
    },
    stopBlinking: () => {
      return new Promise<void>((resolve) => {
        setTimeout(
          () => {
            dispatch(deactivateNodes(nodeIdStrings));
            resolve();
          },
          Math.max(BLINK_TIME - (Date.now() - startTime), 0),
        );
      });
    },
  };
};

export const runExpandThunk = createAsyncThunk<
  void,
  RunExpandPayload,
  {
    state: RootState;
    rejectValue: {
      error: ParseableError;
      nodesCount?: number;
    };
  }
>(`${NAME}/runExpand`, async (payload: RunExpandPayload, { dispatch, getState, rejectWithValue }) => {
  const { globalStore } = StoreKit.UNSAFE_getGlobalStoreInstance();
  const {
    clickedNode,
    labels,
    relTypes,
    direction,
    queryResultLimit,
    timeout,
    autoSelectNewNodes,
    isHttpQueryApiEnabled,
    trackUserQueryEvent = userQueryTypes.expansion,
  } = payload;
  const state = getState();

  const selectedNodes = getSelectedNodes(state);

  const nodesToExpand =
    clickedNode && isMappedNode(clickedNode) && !selectedNodes.includes(clickedNode) ? [clickedNode] : selectedNodes;

  const nodeIdStrings = nodesToExpand.map((n) => n.id);

  const pathSegments = getPathSegments(state);
  const hiddenRelationshipTypes = getHiddenRelationshipTypes(state);
  const visibleLabels = getVisibleLabels(state);
  const perspective = getPerspective(state);
  const database = getDatabase(state);

  if (perspective === undefined || database === undefined) {
    log.warn('Cannot run expand scene if Perspective or database is undefined');
    return rejectWithValue({
      error: {
        name: 'PerspectiveOrDatabaseUndefinedError',
        message: 'Perspective or database is undefined',
        code: 'Unknown.PerspectiveOrDatabaseUndefinedError',
      },
    });
  }

  const relevantRelationshipTypes = relTypes ?? getRelevantRelationshipTypes(pathSegments, hiddenRelationshipTypes);

  if (visibleLabels.length === 0 && relevantRelationshipTypes.length === 0) {
    log.debug('Cannot run expand scene if there are no visible lables or relationship types');
    return rejectWithValue({
      error: {
        name: 'NoVisibleLabelsOrRelationshipTypesError',
        message: 'There are no visible labels or relationship types to expand',
        code: 'Unknown.NoVisibleLabelsOrRelationshipTypesError',
      },
    });
  }

  const { startBlinking, stopBlinking } = handleBlinking(dispatch, nodeIdStrings);

  startBlinking();
  trackUserQueryFromBloomScene(trackUserQueryEvent);

  const visibleNodeIds = getVisibleNodeIds(state);
  const visibleRelationshipIds = getVisibleRelationshipIds(state);

  const serverVersion = getServerVersion(state);
  const isV5OrGreater = isVersion5OorGreater(serverVersion);

  const getGraphElementByIdKey = (element: Node | Relationship) => element[isV5OrGreater ? V5_ID : V4_ID];

  const nodeIds = nodesToExpand.map(getGraphElementByIdKey);
  const schema = getSchemaFromPerspective(perspective);

  let recordLimitHit;
  const totalNodes = new Map<string, Node>();
  const totalRelationships = new Map<string, Relationship>();
  let mappedRecords: Records = { nodes: [], relationships: [] };

  try {
    const { cypherForNewNodes, cypherForExistingNodes, parameters } = getNeighboursForIds(
      nodeIds,
      visibleNodeIds,
      visibleRelationshipIds,
      relevantRelationshipTypes,
      labels ?? visibleLabels,
      schema,
      queryResultLimit,
      serverVersion,
      direction,
      isHttpQueryApiEnabled,
    );

    const recordLimit = (newRecord: DriverCore.Record) => {
      // needed to parse records with excluded properties
      const recordWithGraphType = recordMapper(newRecord);
      mappedRecords = graphMapper([recordWithGraphType]);

      for (const node of mappedRecords.nodes) {
        totalNodes.set(getGraphElementByIdKey(node), node);
      }

      for (const rel of mappedRecords.relationships) {
        totalRelationships.set(getGraphElementByIdKey(rel), rel);
      }

      return totalNodes.size < queryResultLimit;
    };

    const existingNodesQuery = globalStore.dispatch(
      runQuery({
        parameters,
        recordLimit,
        sessionConfig: { database: database.name },
        query: cypherForExistingNodes,
        timeout,
        metadata: { appScope: APP_SCOPE.explore, queryType: QUERY_TYPE.UserAction },
        transactionMode: 'read',
      }),
    );

    await existingNodesQuery.unwrap();

    const newNodesQuery = globalStore.dispatch(
      runQuery({
        parameters,
        recordLimit,
        sessionConfig: { database: database.name },
        query: cypherForNewNodes,
        timeout,
        metadata: { appScope: APP_SCOPE.explore, queryType: QUERY_TYPE.UserAction },
        transactionMode: 'read',
      }),
    );

    const queryResult: QueryResultWithLimit = await newNodesQuery.unwrap();

    recordLimitHit = queryResult.recordLimitHit;
  } catch (error) {
    if (isParseableError(error)) {
      if (isTransactionTimeoutError(error)) {
        void dispatch(
          showNodesAndRelationships({
            nodes: Array.from(totalNodes.values()),
            relationships: Array.from(totalRelationships.values()),
            autoSelectNewNodes,
            zoomOptions: { outOnly: true, noPan: true },
            recordLimitHit,
            error,
          }),
        );
      }

      return rejectWithValue({ error, nodesCount: totalNodes.size });
    }

    return rejectWithValue({ error: UnknownErrors.UnknownError });
  } finally {
    await stopBlinking();
  }

  void dispatch(
    showNodesAndRelationships({
      nodes: Array.from(totalNodes.values()),
      relationships: Array.from(totalRelationships.values()),
      autoSelectNewNodes,
      zoomOptions: { outOnly: true, noPan: true },
      recordLimitHit,
    }),
  );
});

export const runShowGraphThunk = createAsyncThunk<
  void,
  ShowGraphPayload,
  {
    state: RootState;
    rejectValue: {
      error: ParseableError;
      nodesCount?: number;
    };
  }
>(`${NAME}/runShowGraph`, async (payload: ShowGraphPayload, { dispatch, getState, rejectWithValue }) => {
  const { globalStore } = StoreKit.UNSAFE_getGlobalStoreInstance();
  const { labels, queryResultLimit, timeout } = payload;
  const state = getState();
  const globalState = globalStore.getState();
  const isShowMeAGraphOption2Enabled = makeSelectFeatureFlag('explore:show-me-a-graph-option-2')(globalState);
  const isShowMeAGraphOption3Enabled = makeSelectFeatureFlag('explore:show-me-a-graph-option-3')(globalState);
  const pathSegments = getPathSegments(state);
  const database = getDatabase(state);
  if (database === undefined) {
    log.warn('Cannot run show graph scene if database is undefined');
    return rejectWithValue({
      error: {
        name: 'PerspectiveOrDatabaseUndefinedError',
        message: 'Perspective or database is undefined',
        code: 'Unknown.PerspectiveOrDatabaseUndefinedError',
      },
    });
  }

  const serverVersion = getServerVersion(state);
  const isV5OrGreater = isVersion5OorGreater(serverVersion);
  const getGraphElementByIdKey = (element: Node | Relationship) => element[isV5OrGreater ? V5_ID : V4_ID];

  let recordLimitHit;
  const totalNodes = new Map<string, Node>();
  const totalRelationships = new Map<string, Relationship>();
  let mappedRecords: Records = { nodes: [], relationships: [] };

  try {
    const { connectedGraphCyper, unConnectedGraphCypher, parameters } = getSampleSchema(
      labels ?? [],
      pathSegments,
      isShowMeAGraphOption2Enabled,
      isShowMeAGraphOption3Enabled,
    );

    const recordLimit = (newRecord: DriverCore.Record) => {
      // needed to parse records with excluded properties
      const recordWithGraphType = recordMapper(newRecord);
      mappedRecords = graphMapper([recordWithGraphType]);

      for (const node of mappedRecords.nodes) {
        totalNodes.set(getGraphElementByIdKey(node), node);
      }

      for (const rel of mappedRecords.relationships) {
        totalRelationships.set(getGraphElementByIdKey(rel), rel);
      }

      return totalNodes.size < queryResultLimit;
    };

    if (unConnectedGraphCypher) {
      const unConnectedGraphQuery = globalStore.dispatch(
        runQuery({
          parameters,
          recordLimit,
          sessionConfig: { database: database.name },
          query: unConnectedGraphCypher,
          timeout,
          metadata: { appScope: APP_SCOPE.explore, queryType: QUERY_TYPE.UserAction },
          transactionMode: 'read',
        }),
      );
      await unConnectedGraphQuery.unwrap();
    }
    if (connectedGraphCyper) {
      const connectedGraphQuery = globalStore.dispatch(
        runQuery({
          parameters,
          recordLimit,
          sessionConfig: { database: database.name },
          query: connectedGraphCyper,
          timeout,
          metadata: { appScope: APP_SCOPE.explore, queryType: QUERY_TYPE.UserAction },
          transactionMode: 'read',
        }),
      );

      const queryResult: QueryResultWithLimit = await connectedGraphQuery.unwrap();
      recordLimitHit = queryResult.recordLimitHit;
    }
  } catch (error) {
    if (isParseableError(error)) {
      if (isTransactionTimeoutError(error)) {
        void dispatch(
          showNodesAndRelationships({
            nodes: Array.from(totalNodes.values()),
            relationships: Array.from(totalRelationships.values()),
            autoSelectNewNodes: false,
            zoomOptions: { outOnly: true, noPan: true },
            recordLimitHit,
            error,
          }),
        );
      }

      return rejectWithValue({ error, nodesCount: totalNodes.size });
    }

    return rejectWithValue({ error: UnknownErrors.UnknownError });
  }
  void dispatch(
    showNodesAndRelationships({
      nodes: Array.from(totalNodes.values()),
      relationships: Array.from(totalRelationships.values()),
      autoSelectNewNodes: false,
      zoomOptions: { outOnly: true, noPan: true },
      recordLimitHit,
    }),
  );
});
